import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, MapPin, Compass, Sun, Moon, Settings,
  Shield, Crown, LogOut, Target
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { NotificationBell } from "../NotificationBell";
import { WeatherRadioPlayer } from "../WeatherRadioPlayer";
import jaxPilotHead from "../../assets/generated/jetbot_hero.png";

export function DashboardHeader({
  isDarkMode,
  isPremium,
  location,
  searchQuery,
  setSearchQuery,
  isSearching,
  searchResults,
  selectLocation,
  requestGPSLocation,
  toggleTempUnit,
  isCelsius,
  toggleTheme,
  token,
  weather,
  severeAlerts,
  tempSymbol,
  convertTemp,
  setShowDashboardCustomizer,
  user,
  logout,
  onOpenJetRunJax,
}) {
  return (
    <header className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-3 sm:gap-4 mb-5 sm:mb-8" data-testid="dashboard-header">
      <div className="flex w-full xl:w-auto items-center justify-between gap-2 min-w-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <a
            href="https://theforecasttracker.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="theforecasttracker-header-link"
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors group ${isDarkMode ? 'bg-[#00f2ea]/10 hover:bg-[#00f2ea]/20' : 'bg-cyan-100 hover:bg-cyan-200 border border-cyan-200'}`}
            title="Visit TheForecastTracker"
          >
            <img src="https://theforecasttracker.com/icon-512x512.png" alt="TheForecastTracker" className="h-6 w-6 rounded group-hover:scale-110 transition-transform" />
          </a>
          <h1 className="dashboard-brand-title hidden min-w-0 max-w-[62vw] truncate text-[1.35rem] leading-none font-black tracking-tight 2xl:block 2xl:max-w-none 2xl:text-2xl" data-testid="dashboard-brand-title">TheForecastTracker</h1>
        </div>

        <div className="hidden sm:flex items-center gap-2 shrink-0" data-testid="dashboard-partner-links">
          <a href="https://thefarmtracker.com" target="_blank" rel="noopener noreferrer" data-testid="thefarmtracker-header-link" className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors group ${isDarkMode ? 'bg-[#ccff00]/10 hover:bg-[#ccff00]/20' : 'bg-green-100 hover:bg-green-200 border border-green-200'}`} title="Visit TheFarmTracker">
            <img src="https://thefarmtracker.com/farmtracker-logo.png" alt="TheFarmTracker" className="h-6 w-6 rounded group-hover:scale-110 transition-transform" />
          </a>

          <a href="https://sportsbuzzsource.com" target="_blank" rel="noopener noreferrer" data-testid="sportsbuzzsource-header-link" className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors group ${isDarkMode ? 'bg-orange-500/10 hover:bg-orange-500/20' : 'bg-orange-100 hover:bg-orange-200 border border-orange-200'}`} title="Visit SportsBuzzSource">
            <img src="https://sportsbuzzsource.com/sbs-favicon.jpg" alt="SportsBuzzSource" className="h-6 w-6 rounded group-hover:scale-110 transition-transform" />
          </a>

          <a href="https://stringsauce.com" target="_blank" rel="noopener noreferrer" data-testid="stringsauce-header-link" className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors group ${isDarkMode ? 'bg-purple-500/10 hover:bg-purple-500/20' : 'bg-purple-100 hover:bg-purple-200 border border-purple-200'}`} title="Visit StringSauce">
            <img src="https://stringsauce.com/apple-touch-icon.png" alt="StringSauce" className="h-6 w-6 rounded group-hover:scale-110 transition-transform" />
          </a>
        </div>
      </div>

      <div className="grid grid-cols-6 items-center gap-2 w-full xl:w-auto min-w-0 sm:flex sm:flex-wrap sm:gap-3">
        <WeatherRadioPlayer
          location={location}
          weather={weather}
          severeAlerts={severeAlerts}
          isDarkMode={isDarkMode}
          tempSymbol={tempSymbol}
          convertTemp={convertTemp}
        />

        {isPremium && location && (
          <Link to={`/my-land?lat=${location.lat}&lon=${location.lon}&name=${encodeURIComponent(location.name || 'Home')}`} data-testid="my-land-link" className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${isDarkMode ? 'bg-[#00f2ea]/10 text-[#00f2ea] hover:bg-[#00f2ea]/20' : 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200'}`} title="My Tracker">
            <Target className="w-4 h-4" />
            <span className="hidden lg:inline">My Tracker</span>
          </Link>
        )}

        <div className="relative col-span-5 min-w-0 max-w-full sm:flex-1 sm:min-w-[220px] xl:w-72">
          <Search className="absolute left-3.5 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-white/40" />
          <Input type="text" placeholder="Search city..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="location-search-input" className="pl-10 sm:pl-12 h-11 sm:h-12 bg-black/40 backdrop-blur-xl border-white/10 rounded-xl focus:border-[#00f2ea]/50" />
          {isSearching && <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-current/20 border-t-current rounded-full animate-spin" data-testid="location-search-loading" />}

          <AnimatePresence>
            {searchResults.length > 0 && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute top-full mt-2 w-full glass rounded-xl overflow-hidden z-50" data-testid="location-search-results">
                {searchResults.map((result, idx) => (
                  <button key={`${result.lat}-${result.lon}-${result.name}`} onClick={() => selectLocation(result)} data-testid={`search-result-${idx}`} className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-[#00f2ea]" />
                    <span>{result.name}{result.state && `, ${result.state}`}, {result.country}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <Button variant="ghost" size="sm" onClick={requestGPSLocation} data-testid="gps-location-btn" title="Use GPS for precise location" className="h-10 w-full sm:w-auto rounded-xl hover:bg-white/10 px-0 sm:px-3 shrink-0">
          <Compass className="w-5 h-5 text-[#00f2ea]" />
        </Button>

        <Button variant="ghost" size="sm" onClick={toggleTempUnit} data-testid="temp-unit-toggle" className="h-10 w-full sm:w-auto rounded-xl hover:bg-white/10 dark:hover:bg-white/10 light:hover:bg-black/10 font-mono font-bold text-base sm:text-lg px-0 sm:px-3 shrink-0">
          {isCelsius ? "°C" : "°F"}
        </Button>

        <Button variant="ghost" size="sm" onClick={toggleTheme} data-testid="theme-toggle-btn" title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"} className="h-10 w-full sm:w-auto rounded-xl hover:bg-white/10 dark:hover:bg-white/10 px-0 sm:px-3 shrink-0">
          {isDarkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-slate-700" />}
        </Button>

        <NotificationBell token={token} isDarkMode={isDarkMode} />

        {isPremium && (
          <Button variant="ghost" size="sm" onClick={() => setShowDashboardCustomizer(true)} title="Customize Dashboard" data-testid="dashboard-customizer-header-button" className="h-10 w-full sm:w-auto rounded-xl hover:bg-white/10 dark:hover:bg-white/10 px-0 sm:px-3 shrink-0">
            <Settings className="w-5 h-5" />
          </Button>
        )}

        <div className="relative flex h-10 items-center" data-testid="user-game-launch-area">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-10 w-full sm:w-auto rounded-xl hover:bg-white/10 gap-2 px-0 sm:px-3" data-testid="user-menu-btn">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00f2ea] to-[#7000ff] flex items-center justify-center font-bold text-sm text-white shrink-0">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <span className="hidden items-center gap-1.5 md:flex">
                  <span className="opacity-70 text-sm">{user?.name}</span>
                  {isPremium && <Crown className="h-4 w-4 text-yellow-400" data-testid="premium-user-crown-icon" />}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass border-white/10 w-48">
              <Link to="/settings"><DropdownMenuItem className="cursor-pointer" data-testid="settings-menu-item"><Settings className="w-4 h-4 mr-2" />Settings</DropdownMenuItem></Link>
              <Link to="/subscription"><DropdownMenuItem className="cursor-pointer" data-testid="subscription-menu-item"><Crown className="w-4 h-4 mr-2 text-yellow-400" />{user?.subscription?.plan === "premium" ? "Subscription" : "Upgrade to Premium"}</DropdownMenuItem></Link>
              {user?.role === "admin" && <Link to="/admin"><DropdownMenuItem className="cursor-pointer" data-testid="admin-menu-item"><Shield className="w-4 h-4 mr-2 text-[#ff0080]" />Admin Panel</DropdownMenuItem></Link>}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400" data-testid="logout-menu-item"><LogOut className="w-4 h-4 mr-2" />Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            type="button"
            onClick={onOpenJetRunJax}
            data-testid="jet-run-jax-launch-button"
            className={`absolute right-0 top-12 z-20 flex h-9 items-center gap-1.5 rounded-full border px-1.5 pr-3 shadow-xl transition-transform hover:-translate-y-0.5 hover:scale-105 active:translate-y-0 active:scale-95 ${isDarkMode ? 'border-white/20 bg-[#2f3338] text-amber-100 shadow-black/35' : 'border-slate-300 bg-[#5b6168] text-white shadow-slate-900/20'}`}
            aria-label="Launch Jet Run Jax"
            title="Launch Jet Run Jax"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/90" data-testid="jet-run-jax-launch-avatar">
              <img
                src={jaxPilotHead}
                alt="Jax pilot head"
                data-testid="jet-run-jax-launch-image"
                className="h-10 w-10 translate-y-[8px] scale-[1.55] object-contain"
                draggable="false"
              />
            </span>
            <span className="whitespace-nowrap text-[11px] font-black uppercase tracking-wide" data-testid="jet-run-jax-launch-label">Jet Run</span>
          </button>
        </div>
      </div>
    </header>
  );
}